// @ts-strict-ignore
import React from 'react'
import { Helmet } from 'react-helmet-async'

interface CompanyScore {
  score: {
    stars: number
    trustScore: number
  }
  totalReviews: string
}

interface Review {
  text: string
  stars: number
  customer: {
    name: string
  }
}

interface ProductSchema {
  '@context': 'http://schema.org'
  '@type': 'Product'
  name?: string
  brand?: {
    '@type': 'Brand'
    name: string
  }
  aggregateRating?: {
    '@type': 'AggregateRating'
    ratingValue: number
    bestRating: number
    worstRating: number
    ratingCount: number
  }
  reviews?: {
    '@type': 'Review'
    author: { '@type': 'Person'; name: string }
    reviewRating: {
      '@type': 'Rating'
      ratingValue: number
      reviewBody: string
    }
  }[]
}

const MONEY_SCHEMA_PATHS = [
  '/',
  '/savings-accounts',
  '/credit-cards',
  '/current-accounts',
  '/business/bank-accounts',
  '/business/business-loans',
  '/business-insurance',
  '/business/business-savings-accounts',
  '/business/business-credit-cards',
]

const getSchema = (
  companyScore: CompanyScore,
  brandName: string,
  reviews: Review[],
): ProductSchema => {
  let schema: ProductSchema = {
    '@context': 'http://schema.org',
    '@type': 'Product',
  }

  if (companyScore) {
    const { score, totalReviews } = companyScore

    if (brandName) {
      const name = `${brandName.charAt(0).toUpperCase()}${brandName.slice(1)}`
      schema = {
        ...schema,
        name,
        brand: {
          '@type': 'Brand',
          name,
        },
      }
    }

    if (score && score.trustScore && totalReviews) {
      schema = {
        ...schema,
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: score?.trustScore,
          bestRating: 5,
          worstRating: 1,
          ratingCount: parseInt(totalReviews.replace(/,/g, '')),
        },
      }
    }

    if (brandName === 'money' && reviews && reviews.length > 0) {
      schema = {
        ...schema,
        reviews: reviews.map((review: Review) => ({
          '@type': 'Review',
          author: { '@type': 'Person', name: review.customer?.name },
          reviewRating: {
            '@type': 'Rating',
            ratingValue: review.stars,
            reviewBody: review.text,
          },
        })),
      }
    }
  }

  return schema
}

export default ({
  companyScore,
  reviews,
  brandName,
  path,
}: {
  companyScore: CompanyScore
  reviews: Review[]
  brandName: string
  path: string
}) =>
  brandName === 'money' && MONEY_SCHEMA_PATHS.includes(path) ? (
    <Helmet>
      <script type='application/ld+json'>
        {JSON.stringify(getSchema(companyScore, brandName, reviews))}
      </script>
    </Helmet>
  ) : null
